export const ORDER_LENSES_ACTION = 'ORDER LENSES';

export const FIRST_INSPECTION_NEEDED = 'FIRST INSPECTION NEEDED';

export const EXCLUSIVE_JOB_ACTIONS = [
  'SEND TO STACK',
  'HOLD / RX',
  'HOLD',
  'INQUIRY',
  'REPAIR',
  'QUOTE',
  'CONVERT TO ORDER',
];

export const OVERLAY_JOB_ACTIONS = [ORDER_LENSES_ACTION, 'CALL FOR RX'];

export const ROUTING_JOB_ACTION_PRIORITY = [
  'CONVERT TO ORDER',
  'QUOTE',
  'SEND TO STACK',
  'REPAIR',
  'HOLD / RX',
  'HOLD',
  'CALL FOR RX',
  'INQUIRY',
];

export const TASK_STATUS_BY_ACTION: Record<string, string> = {
  'CALL FOR RX': 'CALL / RX',
  [ORDER_LENSES_ACTION]: ORDER_LENSES_ACTION,
  'HOLD / RX': 'HOLD / RX',
  HOLD: 'HOLD',
  INQUIRY: 'INQUIRY',
  QUOTE: 'QUOTE',
  REPAIR: 'REPAIR',
};

export const OPEN_TASK_STATUSES = [
  'CALL / RX',
  'CALL FOR RX',
  ORDER_LENSES_ACTION,
  'INSTALL NOSEPADS',
  'CHECK FRAME ETA',
  FIRST_INSPECTION_NEEDED,
  'HOLD / RX',
  'HOLD',
  'INQUIRY',
  'QUOTE',
  'REPAIR',
];

export function isOpenTaskStatus(status?: string): boolean {
  const value = String(status || '').trim();
  if (!value) {
    return false;
  }
  const normalized = normalizeJobAction(value);
  return (
    OPEN_TASK_STATUSES.includes(value) || OPEN_TASK_STATUSES.includes(normalized)
  );
}

export function taskDisplayName(status?: string): string {
  const value = String(status || '').trim();
  if (value === 'CALL FOR RX' || value === 'CALL / RX') {
    return 'CALL / RX';
  }
  return value;
}

export const AWAITING_FRAME_STATUSES = [
  'AWAITING FRAME',
  'AWAITING PART',
  'AWAITING ACCESSORY',
];

export const AWAITING_LENS_STATUSES = [
  'AWAITING LENSES',
  'AWAITING FRAME + LENSES',
];

export const AWAITING_ETA_STATUSES = [
  ...AWAITING_FRAME_STATUSES,
  ...AWAITING_LENS_STATUSES,
  'AWAITING REPAIR',
];

export function normalizeJobAction(value: string): string {
  const action = String(value || '').trim();
  if (action === 'CALL / RX') {
    return 'CALL FOR RX';
  }
  if (action === 'FRAME ONLY') {
    return '';
  }
  return action;
}

export function jobActionList(
  jobAction?: string,
  jobActions?: string[] | null
): string[] {
  const fromArray = Array.isArray(jobActions)
    ? jobActions.map(normalizeJobAction).filter(Boolean)
    : [];
  if (fromArray.length) {
    return Array.from(new Set(fromArray));
  }
  const single = normalizeJobAction(jobAction);
  return single ? [single] : [];
}

export function hasJobAction(
  jobAction: string | undefined,
  jobActions: string[] | null | undefined,
  action: string
): boolean {
  const wanted = normalizeJobAction(action);
  if (!wanted) {
    return false;
  }
  return jobActionList(jobAction, jobActions).includes(wanted);
}

export function exclusiveJobAction(actions: string[]): string {
  return routingJobAction(actions);
}

export function routingJobAction(actions: string[]): string {
  const set = new Set((actions || []).map(normalizeJobAction).filter(Boolean));
  return ROUTING_JOB_ACTION_PRIORITY.find((action) => set.has(action)) || '';
}

export function withExclusiveJobAction(
  current: string[],
  exclusive: string
): string[] {
  const additive = (current || []).filter((action) =>
    OVERLAY_JOB_ACTIONS.includes(normalizeJobAction(action))
  );
  const next = normalizeJobAction(exclusive);
  if (OVERLAY_JOB_ACTIONS.includes(next)) {
    return Array.from(new Set([...additive, next].filter(Boolean)));
  }
  return next ? [next, ...additive] : additive;
}

export function toggleJobAction(current: string[], option: string, checked: boolean): string[] {
  const action = normalizeJobAction(option);
  if (!action) {
    return jobActionList('', current);
  }
  if (OVERLAY_JOB_ACTIONS.includes(action)) {
    const without = (current || []).filter(
      (value) => normalizeJobAction(value) !== action
    );
    return checked ? [...without, action] : without;
  }
  if (checked) {
    return withExclusiveJobAction(current, action);
  }
  return (current || []).filter((value) => normalizeJobAction(value) !== action);
}

export function isOrderingLenses(
  jobAction?: string,
  jobActions?: string[] | null,
  lensStatus?: string
): boolean {
  const status = String(lensStatus || '').trim();
  if (status === 'RECEIVED') {
    return false;
  }
  return (
    hasJobAction(jobAction, jobActions, ORDER_LENSES_ACTION) ||
    status === 'ORDERED' ||
    status === 'TO ORDER'
  );
}

export function awaitingStatusForType(type?: string): string {
  switch (type) {
    case 'A':
      return 'AWAITING ACCESSORY';
    case 'F':
      return 'AWAITING PART';
    default:
      return 'AWAITING FRAME';
  }
}

export function isFrameAwaitingStatus(status?: string): boolean {
  const value = String(status || '').trim();
  return (
    AWAITING_FRAME_STATUSES.includes(value) ||
    value === 'AWAITING FRAME + LENSES'
  );
}

export function appearsInAwaitingFrames(status?: string): boolean {
  return isFrameAwaitingStatus(status);
}

export function appearsInAwaitingLenses(status?: string): boolean {
  const value = String(status || '').trim();
  return AWAITING_LENS_STATUSES.includes(value);
}

export function combinedAwaitingStatus(opts: {
  type?: string;
  status?: string;
  physicalStatus?: string;
  jobAction?: string;
  jobActions?: string[] | null;
  lensStatus?: string;
  frameAwaiting?: boolean;
}): string {
  const orderingLenses = isOrderingLenses(
    opts.jobAction,
    opts.jobActions,
    opts.lensStatus
  );
  const physical = String(opts.physicalStatus || '').trim();
  const status = String(opts.status || '').trim();
  const frameAwaiting =
    opts.frameAwaiting === true ||
    physical === 'ORDERED' ||
    physical === 'ON-APPROVAL' ||
    isFrameAwaitingStatus(status) ||
    status === 'ORDERED';
  if (frameAwaiting && orderingLenses) {
    return 'AWAITING FRAME + LENSES';
  }
  if (orderingLenses && !frameAwaiting) {
    return 'AWAITING LENSES';
  }
  if (frameAwaiting) {
    return awaitingStatusForType(opts.type);
  }
  return status;
}

export function statusAfterFrameReceived(opts: {
  jobAction?: string;
  jobActions?: string[] | null;
  lensStatus?: string;
}): string {
  return isOrderingLenses(opts.jobAction, opts.jobActions, opts.lensStatus)
    ? 'AWAITING LENSES'
    : 'RECEIVED';
}

export function statusAfterLensesReceived(opts: {
  type?: string;
  status?: string;
  physicalStatus?: string;
}): string {
  const physical = String(opts.physicalStatus || '').trim();
  const status = String(opts.status || '').trim();
  if (
    physical === 'ORDERED' ||
    physical === 'ON-APPROVAL' ||
    isFrameAwaitingStatus(status) ||
    status === 'AWAITING FRAME + LENSES'
  ) {
    return awaitingStatusForType(opts.type);
  }
  return 'RECEIVED';
}
